/** /my/transfer — moving the student's key to another device, and adopting it there.
 *
 * Both directions are student-initiated (STORY-113): the old device shows a QR carrying the
 * student card and the private key sealed under the passphrase chosen at sign-up, and the new
 * device opens it with the same passphrase. No network is involved at any point.
 *
 * The QR is read with the shell's scanner where there is one. On the web there is none, so
 * the same payload is offered as text to paste — web is never second-class, and this keeps
 * the feature inside the dependencies the repo already has.
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { capabilities } from "../platform/capabilities";
import { useSurface } from "../platform/surface";
import { card, importTransfer, SignupError, transferPayload } from "../live/studentIdentity";
import { BottomNav } from "../student/BottomNav";
import { Button, PasswordInput, Qr } from "../ui";

const shortKey = (key: string): string => (key.length > 20 ? `${key.slice(0, 11)}…${key.slice(-6)}` : key);

export function Transfer() {
  const { t } = useTranslation();
  useSurface("paper");
  const [mine, setMine] = useState(card);
  const payload = transferPayload();

  const [pass, setPass] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adopted, setAdopted] = useState(false);

  async function adopt(source: string) {
    setBusy(true);
    setError(null);
    try {
      setMine(await importTransfer(source, pass));
      setAdopted(true);
      setText("");
      setPass("");
    } catch (e) {
      // A wrong passphrase and a mangled code fail in the same place (AES-GCM fails closed),
      // so the two are told apart by the code and named separately.
      setError(e instanceof SignupError && e.code === "not_a_code" ? t("my.transfer.notACode") : t("my.transfer.wrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="font-system mx-auto flex min-h-dvh max-w-md flex-col gap-6 p-4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t("my.transfer.title")}</h1>
        <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("my.transfer.lede")}</p>
      </header>

      {mine && (
        <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
          <h2 className="text-lg font-semibold">{t("my.card.title")}</h2>
          <p className="mt-1 break-words">{mine.display_name}</p>
          <p className="num mt-1 text-xs break-all text-[color:var(--ink-2)]" translate="no">
            {shortKey(mine.student_key)}
          </p>
          <p className="mt-1 text-xs text-[color:var(--ink-2)]">{t("my.card.alg", { alg: mine.alg })}</p>
          {adopted && (
            <p className="mt-2 text-sm" role="status">
              {t("my.transfer.adopted")}
            </p>
          )}
        </section>
      )}

      <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-labelledby="transfer-out">
        <h2 id="transfer-out" className="text-lg font-semibold">
          {t("my.transfer.outTitle")}
        </h2>
        {payload ? (
          <>
            <p className="mt-1 mb-3 text-sm text-[color:var(--ink-2)]">{t("my.transfer.outLede")}</p>
            <div className="flex justify-center">
              <Qr text={payload} size={260} label={t("my.transfer.qrLabel")} />
            </div>
            <details className="mt-3 text-sm">
              <summary className="inline-flex min-h-[var(--control-h)] cursor-pointer items-center">{t("my.transfer.asText")}</summary>
              <p className="mt-1 text-xs text-[color:var(--ink-2)]">{t("my.transfer.asTextHint")}</p>
              <textarea
                readOnly
                rows={4}
                className="field mt-2 w-full font-mono text-xs"
                aria-label={t("my.transfer.qrLabel")}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={payload}
                onFocus={(e) => e.currentTarget.select()}
              />
            </details>
          </>
        ) : mine ? (
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("my.transfer.outSealed")}</p>
        ) : (
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">{t("my.transfer.outNone")}</p>
        )}
      </section>

      <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm" aria-labelledby="transfer-in">
        <h2 id="transfer-in" className="text-lg font-semibold">
          {t("my.transfer.inTitle")}
        </h2>
        <p className="mt-1 mb-3 text-sm text-[color:var(--ink-2)]">{t("my.transfer.inLede")}</p>
        <div className="flex flex-col gap-3">
          <PasswordInput
            label={t("my.transfer.pass")}
            hint={t("my.transfer.passHint")}
            value={pass}
            onChange={setPass}
            autoComplete="current-password"
            name="transfer-passphrase"
          />
          {capabilities.codeScanner && (
            <Button
              type="button"
              variant="secondary"
              block
              disabled={!pass || busy}
              onClick={async () => {
                const scanned = await capabilities.codeScanner!.scan();
                if (scanned) await adopt(scanned);
              }}
            >
              {t("my.transfer.scan")}
            </Button>
          )}
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium">{t("my.transfer.paste")}</span>
            <textarea
              rows={4}
              className="field w-full font-mono text-xs"
              name="transfer-code"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>
              {error}
            </p>
          )}
          <Button type="button" block loading={busy} disabled={!pass || !text.trim()} onClick={() => void adopt(text)}>
            {t("my.transfer.cta")}
          </Button>
        </div>
      </section>

      <p className="text-sm">
        <Link to="/my/classes" className="inline-flex min-h-[var(--control-h)] items-center underline">
          {t("my.transfer.backToClasses")}
        </Link>
      </p>
      <BottomNav />
    </main>
  );
}
